import React, { useState } from 'react';
import axios from 'axios';
import { PaystackButton } from 'react-paystack';
import config from '../config/config';

const AddFundPaymentModal = ({ UserId, UserName, onClose }) => {
  const [paymentMethod, setPaymentMethod] = useState(''); // Dropdown selection
  const [fundAmount, setFundAmount] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');

  const token = localStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const paystackConfig = {
    reference: (new Date()).getTime().toString(),
    email: 'user@example.com',
    amount: fundAmount * 100, // Amount in kobo
    currency: 'NGN', // Set a default currency (Paystack typically uses NGN)
    publicKey: 'pk_test_6d954b11ef91c1d7a8911f46a866de5f37a5771a',
    onSuccess: (reference) => {
      console.log('Paystack success:', reference);
      handlePaymentSuccess('paystack', reference.reference, fundAmount); // Pass fundAmount here
    },
    onClose: () => console.log('Payment window closed'),
  };

  const handleNowPayments = async () => {
    const fund_amount = fundAmount; // Use input amount
    const requestData = {
      price_amount: fund_amount,
      price_currency: 'USD', // Set a default currency for NowPayments
      pay_currency: "btc",
      ipn_callback_url: "https://yourwebsite.com/callback",
      order_id: `order-${(new Date()).getTime()}`,
      order_description: `Subscription for User ID ${UserId}`,
    };

    try {
      const response = await axios.post(
        'https://api-sandbox.nowpayments.io/v1/payment',
        requestData,
        {
          headers: {
            'x-api-key': '11H5KAD-1NC4A54-NG25HG0-Y78N8BS',
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && response.data.payment_id) {
        const { payment_id, payment_url } = response.data;
        window.open(payment_url, '_blank');

        // Polling or checking for payment status
        const interval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(
              `https://api-sandbox.nowpayments.io/v1/payment/${payment_id}`,
              {
                headers: {
                  'x-api-key': '11H5KAD-1NC4A54-NG25HG0-Y78N8BS',
                },
              }
            );
            if (statusResponse.data.status === 'paid') {
              clearInterval(interval);
              handlePaymentSuccess('nowpayment', payment_id, fund_amount); // Pass fund_amount here
            }
          } catch (error) {
            clearInterval(interval);
            setToastMessage('Error checking payment status');
            setToastType('error');
            setTimeout(() => setToastMessage(''), 3000);
          }
        }, 5000); // Poll every 5 seconds

      } else {
        setToastMessage('Failed to initiate NowPayments transaction.');
        setToastType('error');
        setTimeout(() => setToastMessage(''), 3000);
      }
    } catch (error) {
      setToastMessage('Error initiating NowPayments transaction');
      setToastType('error');
      setTimeout(() => setToastMessage(''), 3000);
    }
  };

  const handlePaymentSuccess = async (paymentMethod = '', transaction_id = '', fund_amount) => {
    if (!paymentMethod) {
      setToastMessage('Payment method is required.');
      setToastType('error');
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    setShowLoading(true);
    try {
      const response = await axios.post(`${config.baseURL}/add-funds`, {
        fund_amount: fund_amount, // Pass the correct fund amount
        payment_method: paymentMethod,
        transaction_id: transaction_id,
        currency: 'NGN', // Pass a default currency to backend
      }, { headers });

      if (response.status === 200) {
        setToastMessage('Funds added successfully.');
        setToastType('success');
        setTimeout(() => {
          setToastMessage('');
          onClose(); // Close the modal
          window.location.reload(); // Refresh the page
        }, 3000);
      } else {
        throw new Error('Failed to add funds.');
      }
    } catch (error) {
      console.error('Error during payment success handling:', error);
      setToastMessage('Failed to add funds. Please try again.');
      setToastType('error');
      setTimeout(() => setToastMessage(''), 3000);
    } finally {
      setShowLoading(false);
    }
  };

  const handlePayNowClick = () => {
    document.body.classList.add('payment-processing'); // Add class on button click

    // Check if fundAmount is less than 10
    if (fundAmount < 10) {
      setToastMessage('The minimum amount is 10.');
      setToastType('error');
      setTimeout(() => setToastMessage(''), 3000);
      return; // Exit the function if the amount is too low
    }

    if (paymentMethod === 'paystack') {
      const paystackButton = document.querySelector('.paystack-button');
      if (paystackButton) {
        paystackButton.click();
      }
    } else if (paymentMethod === 'nowpayment') {
      handleNowPayments(); // Handle NowPayments click
    }
  };

  return (
    <>
      <div className="modal-overlay show" onClick={onClose} />
      <div className="modal paymentModal fade show" tabIndex="-1" aria-labelledby="paymentModalLabel" aria-hidden="true" style={{ display: 'block' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="paymentModalLabel">Choose Payment Method</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <p>Select your preferred payment method and enter the amount to fund your account.</p>              
              {/* Input Field for Fund Amount */}
              <div className="form-group mb-3">
                <input
                  type="number"
                  id="fundAmount"
                  value={fundAmount}
                  onChange={(e) => setFundAmount(e.target.value)} // Update fund amount on input change
                  placeholder="Enter amount"
                  className="form-control"
                  min="10" // Set minimum amount
                  required // Make the field required
                />
                {/* Optional: Display a message when the amount is below the minimum */}
                {fundAmount < 10 && fundAmount !== '' && (
                  <small className="text-danger">Minimum amount is NGN 10.</small>
                )}
              </div>

              {/* Dropdown for Payment Method */}
              <div className="form-group mb-3">
                <select
                  id="paymentMethod"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)} // Update payment method on dropdown change
                  className="form-select"
                >
                  <option value="">Select a payment method</option>
                  <option value="paystack">Paystack</option>
                  <option value="nowpayment">NowPayments</option>
                </select>
              </div>

            {showLoading && <div>Loading...</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary send_btn" onClick={handlePayNowClick}>
                Pay Now
              </button> 
              <PaystackButton
                {...paystackConfig}
                text="Pay with Paystack"
                className="d-none paystack-button" // Hide Paystack button
              />
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className={`toast-message ${toastType === 'success' ? 'toast-success' : 'toast-error'}`}>
          {toastMessage}
        </div>
      )}
    </>
  );
};

export default AddFundPaymentModal;
